/**
 * Hook: DELETE /api/t/{slug}/notes/{candidate_id}/{note_id} — per-note
 * soft-delete (P2 demo-friction fix, founder-authorized 2026-06-16;
 * reverses the prior append-only Sec HARD-7 stance).
 *
 * Backend keeps the row (recruiter notes are part of the record), sets
 * `deleted_at`, excludes it from the list, and writes an `activity_log`
 * row. Authz is author-or-admin and enforced server-side; the FE only
 * shows the affordance to users who can act (cosmetic gate).
 *
 * Cache strategy: remove the row from every page of the `useNotes`
 * infinite-query cache (optimistic), rollback on error.
 */
import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
  type InfiniteData,
} from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { ApiError } from "@/lib/apiError";
import { apiClient } from "@/lib/apiClient";
import { useToastStore } from "@/stores/useToastStore";
import type { NotesPage } from "./useNotes";

interface DeleteNoteVars {
  candidate_id: string;
  note_id: string;
}

interface DeleteContext {
  previous: InfiniteData<NotesPage, string | null> | undefined;
}

export function useDeleteNote(): UseMutationResult<
  void,
  ApiError,
  DeleteNoteVars,
  DeleteContext
> {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const queryClient = useQueryClient();
  const addToast = useToastStore((s) => s.addToast);

  return useMutation<void, ApiError, DeleteNoteVars, DeleteContext>({
    mutationFn: async (vars) => {
      await apiClient.delete(
        `/api/t/${tenantSlug}/notes/${vars.candidate_id}/${vars.note_id}`,
      );
    },
    onMutate: async (vars) => {
      const queryKey = [
        "notes",
        "by-candidate",
        tenantSlug,
        vars.candidate_id,
      ] as const;
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<
        InfiniteData<NotesPage, string | null>
      >(queryKey);

      queryClient.setQueryData<InfiniteData<NotesPage, string | null>>(
        queryKey,
        (prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            pages: prev.pages.map((page) => ({
              ...page,
              items: page.items.filter((it) => it.id !== vars.note_id),
            })),
          };
        },
      );
      return { previous };
    },
    onSuccess: () => {
      addToast("Note deleted.", "success");
    },
    onError: (err, vars, context) => {
      const queryKey = [
        "notes",
        "by-candidate",
        tenantSlug,
        vars.candidate_id,
      ] as const;
      if (context?.previous !== undefined) {
        queryClient.setQueryData(queryKey, context.previous);
      }
      if (err instanceof ApiError) {
        if (err.status === 403) {
          addToast("You can only delete your own notes.", "error");
          return;
        }
        if (err.status === 404) {
          addToast("Note not found. Refresh the page.", "error");
          return;
        }
      }
      addToast("Couldn't delete note. Try again.", "error");
    },
  });
}
